import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, FormField, Input } from '@beatq/ui';
import { PermissionGate, showToast } from '@beatq/shared';
import {
  ADJUST_TIME_REASONS,
  CANCEL_REASONS,
  ORDER_BOARD_PERMISSIONS,
  REJECT_REASONS,
} from './catalog.js';
import {
  acceptCountdownLabel,
  acceptDeadlineRemainingMs,
  ACCEPT_ESCALATE_MS,
  acceptOrder,
  adjustPromisedTime,
  cancelConsequenceCopy,
  cancelOrder,
  customerImpactForDelay,
  formatOrderValue,
  getOrder,
  incomingModalDismissible,
  isScheduledOrder,
  lineDisplayName,
  lineIsUnavailable,
  orderFromEnvelope,
  rejectConsequenceCopy,
  rejectOrder,
  reprintTicket,
  scheduledSlotLabel,
  shortOrderLabel,
  type OrderDetail,
  type OrderSummary,
} from './orderBoardApi.js';
import { KitchenTicketPreview } from './OrderDetailUi.js';
import {
  loadPrintQueue,
  pendingPrintCount,
  printHealthFromQueue,
  upsertPrintQueueItem,
} from './printQueue.js';
import { drainPrintQueue } from './printQueueSync.js';

export type BoardModalKind = 'incoming' | 'reject' | 'adjust' | 'print' | 'cancel' | null;

export function BoardModalShell({
  title,
  screenId,
  onClose,
  dismissible = true,
  panelClassName,
  children,
}: {
  title: string;
  screenId: string;
  onClose: () => void;
  dismissible?: boolean;
  panelClassName?: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, dismissible]);

  useEffect(() => {
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    if (!panel) return undefined;
    const focusable = panel.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
    return () => {
      restoreFocusRef.current?.focus?.();
    };
  }, []);

  return (
    <div
      className="mer-board-modal-root"
      data-testid="merchant-board-modal"
      data-screen-id={screenId}
    >
      <button
        type="button"
        className="mer-board-modal-backdrop"
        aria-label="Close dialog"
        onClick={dismissible ? onClose : undefined}
        disabled={!dismissible}
      />
      <div
        ref={panelRef}
        className={`mer-board-modal-panel m08-board-modal-panel${panelClassName ? ` ${panelClassName}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mer-board-modal-title"
      >
        <div className="mer-board-modal-header">
          <h2 id="mer-board-modal-title" className="m-0 text-h3">
            {title}
          </h2>
          {dismissible ? (
            <Button type="button" variant="secondary" className="min-h-11" onClick={onClose}>
              Close
            </Button>
          ) : null}
        </div>
        <div className="mer-board-modal-body">{children}</div>
      </div>
    </div>
  );
}

function IncomingLines({ order }: { order: OrderDetail | OrderSummary }) {
  const detail = order as OrderDetail;
  const lines = detail.lines;
  const value = formatOrderValue(
    order.totalMinor ?? detail.grandTotal?.minor ?? order.total?.minor,
  );
  const itemCount = order.itemCount ?? lines?.length ?? null;
  const unavailableLines = lines?.filter(lineIsUnavailable) ?? [];
  const slot = scheduledSlotLabel(order);
  return (
    <div className="m08-incoming-body">
      <p className="m-0 text-body-sm" data-testid="merchant-incoming-count">
        {order.fulfilmentMode ? `${order.fulfilmentMode} · ` : ''}
        {itemCount != null ? `${itemCount} items` : ''}
        {value ? ` · ${value}` : ''}
        {detail.customer?.isRepeatCustomer ? ' · Repeat customer' : ''}
      </p>
      {slot ? (
        <p className="m-0 text-body-sm" data-testid="merchant-incoming-slot">
          {slot} — not an immediate accept countdown
        </p>
      ) : order.promisedAt ? (
        <p className="m-0 text-body-sm text-muted">
          Promised{' '}
          {new Date(order.promisedAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      ) : null}
      {unavailableLines.length > 0 ? (
        <p className="m08-oos-banner m-0" data-testid="merchant-incoming-oos">
          {unavailableLines.length} item{unavailableLines.length === 1 ? '' : 's'} marked
          unavailable at this branch. Accepting this order is a problem.
        </p>
      ) : null}
      {lines?.length ? (
        <ul className="mer-detail-line-list">
          {lines.map((line, idx) => (
            <li key={line.id ?? `${lineDisplayName(line)}-${idx}`}>
              <span className="mer-detail-line-qty">{line.quantity ?? 1}×</span>
              <span className="mer-detail-line-name">{lineDisplayName(line)}</span>
              {lineIsUnavailable(line) ? <span className="m08-oos-flag"> Unavailable</span> : null}
              {line.modifiers?.length ? (
                <ul className="m08-modifier-list">
                  {line.modifiers.map((mod) => (
                    <li key={mod.name}>{mod.name}</li>
                  ))}
                </ul>
              ) : null}
              {line.notes ? <span className="text-body-sm"> — {line.notes}</span> : null}
              {line.allergens?.length ? (
                <span className="m08-allergen-flag"> Allergen: {line.allergens.join(', ')}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : order.itemSummary ? (
        <p className="m-0">{order.itemSummary}</p>
      ) : null}
      {order.hasAllergenNote ? (
        <p className="m08-allergen-flag m-0">Allergen note on this order</p>
      ) : null}
    </div>
  );
}

export function IncomingOrderModal({
  order,
  onClose,
  onAccepted,
  clockOffsetMs = 0,
}: {
  order: OrderSummary;
  onClose: () => void;
  onAccepted: (id: string) => void;
  clockOffsetMs?: number;
}) {
  const qc = useQueryClient();
  const nowTick = useNow(1000);
  const detailQuery = useQuery({
    queryKey: ['merchant', 'order', order.id],
    queryFn: () => getOrder(order.id!),
    enabled: !!order.id,
    refetchInterval: 10_000,
  });
  const detailed = orderFromEnvelope(detailQuery.data) ?? order;
  const remaining = acceptDeadlineRemainingMs(detailed, nowTick, clockOffsetMs);
  const scheduled = isScheduledOrder(detailed);
  const urgent = remaining != null && remaining <= ACCEPT_ESCALATE_MS && !scheduled;
  const expired = remaining != null && remaining <= 0 && !scheduled;
  const [step, setStep] = useState<'decide' | 'reject'>('decide');

  const accept = useMutation({
    mutationFn: (prepMinutes: number | null) =>
      acceptOrder(order.id!, prepMinutes != null ? { prepMinutes } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['merchant', 'orders'] });
      showToast('success', 'Order accepted');
      onAccepted(order.id!);
    },
    onError: (err) => showToast('error', (err as Error).message),
  });

  if (step === 'reject') {
    return <RejectOrderModal order={detailed} onClose={onClose} onDone={onClose} />;
  }

  return (
    <BoardModalShell
      title="Incoming order"
      screenId="SCR-MER-051"
      onClose={onClose}
      dismissible={incomingModalDismissible(detailed, expired)}
      panelClassName="m08-incoming-modal"
    >
      {remaining != null && !scheduled ? (
        <p
          className={`mer-accept-countdown-panel${urgent || expired ? ' mer-accept-countdown-urgent' : ''}`}
          data-testid="merchant-accept-countdown"
          data-urgent={urgent || expired ? 'true' : 'false'}
        >
          {acceptCountdownLabel(remaining)}
        </p>
      ) : null}
      <div className="m08-incoming-scroll">
        {detailQuery.isLoading && !(detailed as OrderDetail).lines?.length ? (
          <p className="text-muted m-0">Loading items…</p>
        ) : null}
        <IncomingLines order={detailed} />
        {expired ? (
          <p className="m-0" data-testid="merchant-incoming-expired">
            The accept window has ended. The order expired and a refund has been started. Dismiss
            this panel.
          </p>
        ) : detailed.claimedByName ? (
          <p className="m08-claimed m-0">Accepted by {detailed.claimedByName}</p>
        ) : null}
      </div>
      {expired ? (
        <div className="m08-incoming-actions">
          <Button type="button" className="min-h-12" onClick={onClose}>
            Dismiss
          </Button>
        </div>
      ) : detailed.claimedByName ? null : (
        <div className="m08-incoming-actions">
          <PermissionGate
            perm={ORDER_BOARD_PERMISSIONS['SCR-MER-051']?.[0]}
            disabledReason="You need permission to accept orders"
          >
            <Button
              type="button"
              className="min-h-12"
              disabled={accept.isPending}
              data-testid="merchant-incoming-accept"
              onClick={() => accept.mutate(null)}
            >
              {accept.isPending ? 'Accepting…' : 'Accept'}
            </Button>
            <div className="flex flex-wrap gap-inline">
              {([10, 15, 20] as const).map((mins) => (
                <Button
                  key={mins}
                  type="button"
                  variant="secondary"
                  className="min-h-12"
                  disabled={accept.isPending}
                  data-testid={`merchant-incoming-accept-longer-${mins}`}
                  onClick={() => accept.mutate(mins)}
                >
                  Accept +{mins}m
                </Button>
              ))}
            </div>
          </PermissionGate>
          <PermissionGate
            perm={ORDER_BOARD_PERMISSIONS['SCR-MER-052']?.[0]}
            disabledReason="You need permission to reject orders"
          >
            <Button
              type="button"
              variant="secondary"
              className="min-h-12 m08-reject-subordinate"
              disabled={accept.isPending}
              data-testid="merchant-incoming-reject"
              onClick={() => setStep('reject')}
            >
              Reject
            </Button>
          </PermissionGate>
        </div>
      )}
    </BoardModalShell>
  );
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function RejectOrderModal({
  order,
  onClose,
  onDone,
}: {
  order: OrderSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState<string>(REJECT_REASONS[0]);
  const [notes, setNotes] = useState('');
  const reject = useMutation({
    mutationFn: () =>
      rejectOrder(order.id!, {
        reason,
        comment: notes.trim() || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['merchant', 'orders'] });
      showToast('success', 'Order rejected');
      onDone();
    },
    onError: (err) => showToast('error', (err as Error).message),
  });

  return (
    <BoardModalShell title="Reject order" screenId="SCR-MER-052" onClose={onClose}>
      <p className="text-body-sm text-muted">
        Rejecting <strong>{shortOrderLabel(order)}</strong>
      </p>
      <p className="m08-consequence" data-testid="merchant-reject-consequence">
        {rejectConsequenceCopy(reason)}
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (reason === 'OTHER' && !notes.trim()) {
            showToast('error', 'Add a comment for Other');
            return;
          }
          reject.mutate();
        }}
      >
        <FormField label="Reason" htmlFor="modalRejectReason" required>
          <select
            id="modalRejectReason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="merchant-reject-reason"
          >
            {REJECT_REASONS.map((option) => (
              <option key={option} value={option}>
                {option.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Notes" htmlFor="modalRejectNotes">
          <Input id="modalRejectNotes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
        <Button type="submit" className="min-h-12" disabled={reject.isPending}>
          Confirm rejection
        </Button>
      </form>
    </BoardModalShell>
  );
}

export function AdjustTimeModal({
  order,
  onClose,
  onDone,
}: {
  order: OrderSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [extraMinutes, setExtraMinutes] = useState('10');
  const [reason, setReason] = useState<string>(ADJUST_TIME_REASONS[0]);
  const mins = Number(extraMinutes) || 0;
  const save = useMutation({
    mutationFn: () =>
      adjustPromisedTime(order.id!, {
        additionalMinutes: mins,
        reason,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['merchant', 'orders'] });
      void qc.invalidateQueries({ queryKey: ['merchant', 'order', order.id] });
      showToast('success', 'Promised time updated');
      onDone();
    },
    onError: (err) => showToast('error', (err as Error).message),
  });

  return (
    <BoardModalShell title="Adjust promised time" screenId="SCR-MER-054" onClose={onClose}>
      <p className="text-body-sm text-muted">
        Order <strong>{shortOrderLabel(order)}</strong>
        {order.promisedAt ? ` · current ${new Date(order.promisedAt).toLocaleString()}` : ''}
      </p>
      <p className="m08-consequence" data-testid="merchant-adjust-impact">
        {customerImpactForDelay(mins, order.promisedAt)}
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormField label="Additional minutes" htmlFor="modalExtraMinutes" required>
          <Input
            id="modalExtraMinutes"
            value={extraMinutes}
            onChange={(e) => setExtraMinutes(e.target.value)}
            data-testid="merchant-adjust-minutes"
          />
        </FormField>
        <FormField label="Reason" htmlFor="modalDelayReason" required>
          <select
            id="modalDelayReason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="merchant-adjust-reason"
          >
            {ADJUST_TIME_REASONS.map((option) => (
              <option key={option} value={option}>
                {option.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </FormField>
        <Button type="submit" className="min-h-12" disabled={save.isPending}>
          Update promised time
        </Button>
      </form>
    </BoardModalShell>
  );
}

export function CancelOrderModal({
  order,
  onClose,
  onDone,
}: {
  order: OrderSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState<string>(CANCEL_REASONS[0]);
  const [notes, setNotes] = useState('');
  const [ack, setAck] = useState(false);
  const cancel = useMutation({
    mutationFn: () =>
      cancelOrder(order.id!, {
        reason,
        comment: notes.trim() || undefined,
        acknowledgeImpact: true,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['merchant', 'orders'] });
      showToast('success', 'Order cancelled');
      onDone();
    },
    onError: (err) => showToast('error', (err as Error).message),
  });

  return (
    <BoardModalShell title="Cancel order" screenId="SCR-MER-056" onClose={onClose}>
      <p className="text-body-sm text-muted">
        Cancelling <strong>{shortOrderLabel(order)}</strong>
      </p>
      <p className="m08-consequence" data-testid="merchant-cancel-consequence">
        {cancelConsequenceCopy()}
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!ack) {
            showToast('error', 'Acknowledge the refund and reliability impact to continue');
            return;
          }
          cancel.mutate();
        }}
      >
        <FormField label="Reason" htmlFor="modalCancelReason" required>
          <select
            id="modalCancelReason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="merchant-cancel-reason"
          >
            {CANCEL_REASONS.map((option) => (
              <option key={option} value={option}>
                {option.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Notes" htmlFor="modalCancelNotes">
          <Input id="modalCancelNotes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
        <label className="m08-ack" htmlFor="modalCancelAck">
          <input
            id="modalCancelAck"
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            data-testid="merchant-cancel-ack"
          />
          I understand the customer is refunded and this counts against reliability
        </label>
        <Button type="submit" className="min-h-12" disabled={cancel.isPending || !ack}>
          Confirm cancellation
        </Button>
      </form>
    </BoardModalShell>
  );
}

export function PrintTicketModal({
  order,
  onClose,
  autoPrint,
}: {
  order: OrderSummary;
  onClose: () => void;
  autoPrint?: boolean;
}) {
  const autoFired = useRef(false);
  const [queueDepth, setQueueDepth] = useState(() => pendingPrintCount());
  const health = printHealthFromQueue();
  const reprint = useMutation({
    mutationFn: () => reprintTicket(order.id!),
    onMutate: () => {
      if (!order.id) return;
      upsertPrintQueueItem({
        id: `print-${order.id}`,
        orderId: order.id,
        label: order.publicReference ?? order.id,
        status: 'pending',
        updatedAt: new Date().toISOString(),
      });
      setQueueDepth(pendingPrintCount());
    },
    onSuccess: () => {
      if (order.id) {
        upsertPrintQueueItem({
          id: `print-${order.id}`,
          orderId: order.id,
          label: order.publicReference ?? order.id,
          status: 'printed',
          updatedAt: new Date().toISOString(),
        });
      }
      setQueueDepth(pendingPrintCount());
      showToast('success', 'Ticket print requested');
    },
    onError: (err) => {
      if (order.id) {
        upsertPrintQueueItem({
          id: `print-${order.id}`,
          orderId: order.id,
          label: order.publicReference ?? order.id,
          status: 'failed',
          updatedAt: new Date().toISOString(),
        });
        setQueueDepth(pendingPrintCount());
      }
      setQueueDepth(pendingPrintCount());
      showToast('error', (err as Error).message);
    },
  });

  useEffect(() => {
    if (!autoPrint || !order.id || autoFired.current) return;
    autoFired.current = true;
    reprint.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot
  }, [autoPrint, order.id]);

  const failed = loadPrintQueue().some((q) => q.orderId === order.id && q.status === 'failed');

  return (
    <BoardModalShell title="Print ticket" screenId="SCR-MER-058" onClose={onClose}>
      <p className="mer-print-health m-0" data-health={health} data-testid="merchant-print-health">
        Printer: <strong>{health}</strong>
        {queueDepth > 0 ? ` · offline queue ${queueDepth}` : ''}
      </p>
      <KitchenTicketPreview order={order} reprint={Boolean(autoPrint || failed)} />
      <div className="flex flex-wrap gap-inline">
        <Button
          type="button"
          className="min-h-12"
          disabled={reprint.isPending || health === 'offline'}
          data-testid="merchant-print-modal-reprint"
          onClick={() => reprint.mutate()}
        >
          {reprint.isPending
            ? 'Printing…'
            : failed
              ? 'Retry failed print'
              : 'Print / reprint ticket'}
        </Button>
        {health === 'offline' || failed ? (
          <Button
            type="button"
            variant="secondary"
            className="min-h-12"
            onClick={() => {
              if (!order.id) return;
              upsertPrintQueueItem({
                id: `print-${order.id}`,
                orderId: order.id,
                label: order.publicReference ?? order.id,
                status: 'printed',
                updatedAt: new Date().toISOString(),
              });
              setQueueDepth(pendingPrintCount());
              showToast('success', 'Marked as printed manually');
            }}
          >
            Mark as printed
          </Button>
        ) : null}
        {queueDepth > 0 && health !== 'offline' ? (
          <Button
            type="button"
            variant="secondary"
            className="min-h-12"
            onClick={() => void drainPrintQueue().then(() => setQueueDepth(pendingPrintCount()))}
          >
            Print queued
          </Button>
        ) : null}
        <Button type="button" variant="secondary" className="min-h-12" onClick={onClose}>
          Done
        </Button>
      </div>
    </BoardModalShell>
  );
}
